import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import PropTypes from 'prop-types';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from '@mui/material';

import { CloseOutlined } from '@mui/icons-material';

import DiagnosisSearch from './DiagnosisSearch';
import DiagnosisList from './DiagnosisList';
import SelectedDiagnosisList from './SelectedDiagnosisList';

import { dialogHeaderStyles } from '../Styles/InputStyles';
import CommonDialogActions from '../CommonDialogActions';

import { defaultICD10Diagnoses } from './diagnosisData';
import { diagnosisDialogStyles } from './diagnosisStyles';

const ICD10DiagnosisDialog = ({
  open,
  diagnoses,
  selectedDiagnoses,
  onClose,
  onSave
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [availableDiagnoses, setAvailableDiagnoses] =
    useState(diagnoses);

  const [temporarySelected, setTemporarySelected] =
    useState([]);

  useEffect(() => {
    if (!open) return;

    setSearchValue('');
    setAvailableDiagnoses(diagnoses);
    setTemporarySelected(selectedDiagnoses);
  }, [open, diagnoses, selectedDiagnoses]);

  const selectedDiagnosisIds = useMemo(
    () => temporarySelected.map(item => item.id),
    [temporarySelected]
  );

  const filteredDiagnoses = useMemo(() => {
    const normalizedSearch = searchValue
      .trim()
      .toLowerCase();

    if (!normalizedSearch) {
      return availableDiagnoses;
    }

    return availableDiagnoses.filter(item => {
      const code = item.code.toLowerCase();
      const description = item.description.toLowerCase();

      return (
        code.includes(normalizedSearch) ||
        description.includes(normalizedSearch)
      );
    });
  }, [availableDiagnoses, searchValue]);

  const handleSelectDiagnosis = diagnosis => {
    setTemporarySelected(currentDiagnoses => {
      const alreadySelected = currentDiagnoses.some(
        item => item.id === diagnosis.id
      );

      if (alreadySelected) {
        return currentDiagnoses;
      }

      return [...currentDiagnoses, diagnosis];
    });
  };

  const handleRemoveDiagnosis = diagnosisId => {
    setTemporarySelected(currentDiagnoses =>
      currentDiagnoses.filter(
        item => item.id !== diagnosisId
      )
    );
  };

  const handleAddDiagnosis = diagnosis => {
    setAvailableDiagnoses(currentDiagnoses => {
      const duplicate = currentDiagnoses.some(
        item =>
          item.code.toLowerCase() ===
          diagnosis.code.toLowerCase()
      );

      if (duplicate) {
        return currentDiagnoses;
      }

      return [diagnosis, ...currentDiagnoses];
    });

    handleSelectDiagnosis(diagnosis);
  };

  const handleCancel = () => {
    setTemporarySelected(selectedDiagnoses);
    setSearchValue('');
    onClose();
  };

  const handleSave = () => {
    onSave(temporarySelected);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth={false}
      scroll="paper"
      slotProps={{
        paper: {
          sx: diagnosisDialogStyles.paper
        }
      }}>
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>
            Diagnosis
          </Typography>

          <Typography sx={dialogHeaderStyles.subtitle}>
           Search and select ICD-10 diagnoses for the encounter.
          </Typography>
        </Box>

        <IconButton
          size="small"
          aria-label="Close diagnosis dialog"
          onClick={handleCancel}
          sx={dialogHeaderStyles.dialogheaderclose}>
          <CloseOutlined sx={dialogHeaderStyles.closeIcon} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={diagnosisDialogStyles.content}>
        <Grid
          container
          spacing={1.5}
          sx={diagnosisDialogStyles.contentGrid}>
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={diagnosisDialogStyles.gridItem}>
            <Box sx={diagnosisDialogStyles.panel}>
              <Box sx={diagnosisDialogStyles.panelHeader}>
                <Typography
                  sx={diagnosisDialogStyles.panelTitle}>
                  Diagnosis
                </Typography>
              </Box>

              <DiagnosisSearch
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                onAddDiagnosis={handleAddDiagnosis}
              />

              <DiagnosisList
                diagnoses={filteredDiagnoses}
                selectedDiagnosisIds={selectedDiagnosisIds}
                onSelect={handleSelectDiagnosis}
              />
            </Box>
          </Grid>

          <Grid
            size={{ xs: 12, md: 6 }}
            sx={diagnosisDialogStyles.gridItem}>
            <Box sx={diagnosisDialogStyles.panel}>
              <Box sx={diagnosisDialogStyles.panelHeader}>
                <Typography
                  sx={diagnosisDialogStyles.panelTitle}>
                  Selected Diagnosis
                </Typography>
              </Box>

              <SelectedDiagnosisList
                diagnoses={temporarySelected}
                onRemove={handleRemoveDiagnosis}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <CommonDialogActions
        onCancel={handleCancel}
        onSubmit={handleSave}
        submitText="Save"
        disabled={!temporarySelected.length}
      />
    </Dialog>
  );
};

const diagnosisPropType = PropTypes.shape({
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]).isRequired,
  code: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
});

ICD10DiagnosisDialog.propTypes = {
  open: PropTypes.bool,
  diagnoses: PropTypes.arrayOf(diagnosisPropType),
  selectedDiagnoses: PropTypes.arrayOf(
    diagnosisPropType
  ),
  onClose: PropTypes.func,
  onSave: PropTypes.func
};

ICD10DiagnosisDialog.defaultProps = {
  open: false,
  diagnoses: defaultICD10Diagnoses,
  selectedDiagnoses: [],
  onClose: () => {},
  onSave: () => {}
};

export default ICD10DiagnosisDialog;